// The Keys library screen (amendment 2026-10-09; SCR-591, REQ-240): the keys people and machines
// use on the org's proxies, in sub-tabs (ADR-115): Issued keys (the people's keys: the proxy's own
// list, which its shell passes in; staff see them per installation, and the screen links there) ·
// API keys (the org's credentials) · Key settings (API-key sign-in, AWS access keys, key policy) ·
// Trusted tokens. Each settings sub-tab saves only its own paths. Sign-in methods live on the
// Sign-in screen. No secret is shown but a new API key's, once, in its dialog; a trusted token is
// checked against the issuer's public keys, and no id is shown.
import type { ComponentChildren } from "preact";
import { useState } from "preact/hooks";
import { Checkbox, errorId, Field, Fieldset } from "../../primitives/field";
import type { FieldErrors } from "../../primitives/forms";
import type { HelpId } from "../../primitives/help";
import { Link } from "../../primitives/link";
import { Card } from "../../primitives/page";
import { NotFound } from "../../primitives/states";
import { firstVisible, SubTabs, type SubTabDef } from "../../primitives/subtabs";
import { Credentials } from "./credentials";
import { ScopedSettings, type Editor, type SettingsScope, type SignInScreenProps } from "./editor";
import { KEY_SECTIONS } from "./model";
import { blankTrustedToken, tokenErrorKey, TOKEN_ALGORITHMS, TOKEN_MODES, toggleAlgorithm, TRUSTED_TOKENS_MAX, type TrustedTokenMode, type TrustedTokenValues } from "./tokens";

const TOKENS_HELP: HelpId = "customers.trustedTokens";
const TRUSTED_TOKENS_PATH = "sign_in.trusted_tokens";

type TextField = "label" | "issuer" | "jwksUri" | "audiences" | "subjectClaim" | "emailClaim" | "groupsClaim" | "allowedSubjects" | "maxTokenLifeSeconds";

interface TrustedTokenRowProps {
  index: number;
  row: TrustedTokenValues;
  errors: FieldErrors;
  disabled: boolean;
  onChange(next: TrustedTokenValues): void;
  onRemove(): void;
}

function TrustedTokenRow({ index, row, errors, disabled, onChange, onRemove }: TrustedTokenRowProps) {
  const id = (field: string) => `trusted-${index}-${field}`;
  const err = (field: string) => errors[tokenErrorKey(index, field)];
  const text = (field: TextField, label: string, help: HelpId, placeholder?: string, rows?: number) => {
    const control = {
      id: id(field),
      class: "input-mono",
      placeholder,
      value: row[field],
      disabled,
      "aria-invalid": err(field) ? true : undefined,
      "aria-describedby": err(field) ? errorId(id(field)) : undefined,
    };
    return (
      <Field id={id(field)} label={label} help={help} error={err(field)}>
        {rows ? (
          <textarea {...control} rows={rows} onInput={(event) => onChange({ ...row, [field]: event.currentTarget.value })} />
        ) : (
          <input {...control} autocomplete="off" onInput={(event) => onChange({ ...row, [field]: event.currentTarget.value })} />
        )}
      </Field>
    );
  };
  const name = row.label.trim() || `Trusted issuer ${index + 1}`;

  return (
    <Fieldset label={name} help={TOKENS_HELP}>
      <div class="split-even">
        {text("label", "Label", TOKENS_HELP, "Trusted token")}
        {text("issuer", "Issuer", "customers.tokenIssuer", "https://token.actions.githubusercontent.com")}
        {text("jwksUri", "JWKS URL", "customers.tokenJwks")}
        {text("audiences", "Audiences", "customers.tokenAudiences", "nexus", 2)}
        {text("subjectClaim", "Subject claim", "customers.tokenClaims", "sub")}
        {text("emailClaim", "Email claim", "customers.tokenClaims", "none")}
        {text("groupsClaim", "Groups claim", "customers.tokenClaims", "none")}
        {text("allowedSubjects", "Allowed subjects", "customers.tokenSubjects", "repo:acme/*", 2)}
        {text("maxTokenLifeSeconds", "Longest token life (seconds)", "customers.tokenLife")}
        <Field id={id("mode")} label="How the token is used" help="customers.tokenMode">
          <select id={id("mode")} value={row.mode} disabled={disabled} onChange={(event) => onChange({ ...row, mode: event.currentTarget.value as TrustedTokenMode })}>
            {TOKEN_MODES.map((mode) => (
              <option key={mode.value} value={mode.value}>
                {mode.label}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <Fieldset label="Algorithms" help="customers.tokenAlgorithms" error={err("algorithms")}>
        <div class="row">
          {TOKEN_ALGORITHMS.map((alg) => (
            <Checkbox
              key={alg}
              id={id(`alg-${alg}`)}
              label={alg}
              help="customers.tokenAlgorithms"
              checked={row.algorithms.includes(alg)}
              disabled={disabled}
              onChange={() => onChange({ ...row, algorithms: toggleAlgorithm(row.algorithms, alg) })}
            />
          ))}
        </div>
      </Fieldset>
      <Checkbox
        id={id("internal")}
        label="Internal issuer (may resolve to a private address)"
        help="customers.tokenInternal"
        checked={row.internal}
        disabled={disabled}
        onChange={(checked) => onChange({ ...row, internal: checked })}
      />
      <div class="row">
        <button type="button" class="button button-ghost button-sm" aria-label={`Remove ${name}`} disabled={disabled} onClick={onRemove}>
          Remove
        </button>
      </div>
    </Fieldset>
  );
}

function TrustedTokens({ editor }: { editor: Editor }) {
  const mode = editor.modeOf(TRUSTED_TOKENS_PATH);
  const disabled = editor.readOnly || mode.kind === "readonly";
  const rows = editor.tokens;
  return (
    <Card title="Trusted tokens" help={TOKENS_HELP}>
      <Fieldset label="Trusted issuers" help={TOKENS_HELP} error={editor.errors["trustedTokens"] ?? editor.errors[TRUSTED_TOKENS_PATH]}>
        {mode.kind === "readonly" && <p class="card-note">{mode.note}.</p>}
        {rows.length === 0 && <p class="card-note">No trusted issuers.</p>}
        {rows.map((row, index) => (
          <TrustedTokenRow
            key={row.id || `new-${index}`}
            index={index}
            row={row}
            errors={editor.errors}
            disabled={disabled}
            onChange={(next) => editor.setTokens(rows.map((r, i) => (i === index ? next : r)))}
            onRemove={() => editor.setTokens(rows.filter((_, i) => i !== index))}
          />
        ))}
        <div class="row">
          <button type="button" class="button button-sm" disabled={disabled || rows.length >= TRUSTED_TOKENS_MAX} onClick={() => editor.setTokens([...rows, blankTrustedToken()])}>
            Add trusted issuer
          </button>
        </div>
      </Fieldset>
    </Card>
  );
}

function KeySettingsCards(editor: Editor) {
  return (
    <>
      {KEY_SECTIONS.map((section) => (
        <Card key={section.title} title={section.title} help={section.help}>
          <div class="settings-grid">{section.paths.map(editor.field)}</div>
        </Card>
      ))}
    </>
  );
}

function TrustedTokensCards(editor: Editor) {
  return <TrustedTokens editor={editor} />;
}

/** What the Keys sub-tabs' visibility reads: where the screen is mounted. */
export interface KeysScope {
  /** The shell holds the people's issued keys (the proxy); staff see them per installation instead. */
  issued: boolean;
}

/** The Keys sub-tabs (NAV-042), in display order. Issued keys is the proxy's alone. */
export const KEY_SUBTABS: readonly SubTabDef<KeysScope>[] = [
  { id: "issued", label: "Issued keys", visible: (scope) => scope.issued },
  { id: "api-keys", label: "API keys", visible: () => true },
  { id: "settings", label: "Key settings", visible: () => true },
  { id: "trusted-tokens", label: "Trusted tokens", visible: () => true },
];

type KeySettingsTab = "settings" | "trusted-tokens";

/** What each settings sub-tab saves: only these paths. */
export const KEY_SUBTAB_PATHS: Readonly<Record<KeySettingsTab, readonly string[]>> = {
  settings: KEY_SECTIONS.flatMap((s) => s.paths),
  "trusted-tokens": [TRUSTED_TOKENS_PATH],
};

/** The paths the Keys screen edits, over all its sub-tabs. */
export const KEY_PATHS: readonly string[] = [...KEY_SUBTAB_PATHS.settings, ...KEY_SUBTAB_PATHS["trusted-tokens"]];

const KEY_CARDS: Readonly<Record<KeySettingsTab, (editor: Editor) => ComponentChildren>> = { settings: KeySettingsCards, "trusted-tokens": TrustedTokensCards };

function isSettingsTab(id: string | undefined): id is KeySettingsTab {
  return id === "settings" || id === "trusted-tokens";
}

export interface KeysScreenProps extends SignInScreenProps {
  /** The route's sub-tab segment; undefined at the section's bare URL. */
  subtab: string | undefined;
  /** The shell's URL of one of the sub-tabs. */
  subtabHref(id: string): string;
  /** The people's issued keys (the proxy's `KeysScreen`); absent on staff, where the row links instead. */
  issued?: ComponentChildren;
  /** Staff: where each installation's People & keys are (the customer's AWS accounts tab). */
  installationsHref?: string;
}

export function OrgKeysScreen({ context, scope: initialScope, subtab, subtabHref, issued, installationsHref }: KeysScreenProps) {
  // The scope the AllCode admin's selector chose stays as the person moves between sub-tabs.
  const [scope, setScope] = useState<SettingsScope>(initialScope);
  const keysScope: KeysScope = { issued: issued !== undefined };
  const current = subtab ?? firstVisible(KEY_SUBTABS, keysScope);
  const shown = KEY_SUBTABS.find((t) => t.id === current && t.visible(keysScope))?.id;
  return (
    <>
      <SubTabs tabs={KEY_SUBTABS} scope={keysScope} current={current} href={subtabHref} label="Keys" />
      {installationsHref !== undefined && (
        <p class="card-note">
          People's issued keys are kept per installation: open one under <Link href={installationsHref}>AWS accounts</Link> for its People &amp; keys.
        </p>
      )}
      {shown === "issued" && issued}
      {shown === "api-keys" && <Credentials context={context} />}
      {isSettingsTab(shown) && (
        // Keyed: each sub-tab loads, drafts and saves on its own.
        <ScopedSettings key={shown} context={context} scope={scope} onScopeChange={setScope} label="Key settings" saveLabel="Save key settings" loadingLabel="Loading key settings…" owns={KEY_SUBTAB_PATHS[shown]}>
          {KEY_CARDS[shown]}
        </ScopedSettings>
      )}
      {shown === undefined && <NotFound />}
    </>
  );
}

